import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ConfirmDialog from '../../ConfirmDialog';
import CyclingLoader from '../../shared/CyclingLoader';
import useBarcodeScanner from '../../shared/useBarcodeScanner';
import ScanStepBreadcrumb from '../../shared/ScanStepBreadcrumb';
import { getScannerStatusText } from '../../shared/barcodeScanner';
import { formatApiDateTime } from '../../../helpers/helpers';
import { extractRaceCsvFileMeta } from '../utils/athleteAssignmentsMeta';
import {
  parseAthletesCsv,
  parseDevicePackageAssignmentsCsv,
  serializeDevicePackageAssignmentsCsv,
  type DevicePackageAssignmentCsvRow,
} from '../utils/athleteAssignmentsCsvParse';
import { markRaceStackedOverlayJustClosed } from '../utils/raceDetailPortalHelpers';
import {
  fleetDeviceScanErrorMessage,
  resolveFleetDeviceScan,
  resolveRacerangerScannedDeviceId,
  SCAN_DEVICE_EXPECT_BIB_FIRST,
} from '../../../helpers/racerangerDeviceScan';
import { isFrontDeviceId, isRearDeviceId } from '../../../helpers/devicePairing';
import ScannerEntryModeToggle from '../../shared/ScannerEntryModeToggle';
import RearDeviceValidationBadge, {
  type RearDeviceValidationUiState,
} from './RearDeviceValidationBadge';
import {
  parseRaceAthletesApiResponse,
  raceAthletesToPackageAssignmentRows,
} from '../utils/raceAthletes';
import { BTN_PRIMARY, BTN_SECONDARY, dispatchToast, dispatchToastError, formatApiResponseError } from '../utils';

const REAR_VALIDATION_MIN_SPINNER_MS = 180;

const PHASE_PULSE_CELL =
  'animate-pulse rounded bg-blue-100/70 px-1 font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
const PHASE_PULSE_INPUT =
  'animate-pulse border-blue-400 ring-2 ring-blue-400/60 bg-blue-50/80 dark:border-blue-400 dark:bg-blue-950/50';

type ScanPhase = 'raceNo' | 'rear' | 'front';
type EntryMode = 'scan' | 'manual';

const SCAN_STEPS: { key: ScanPhase; label: string }[] = [
  { key: 'raceNo', label: '1 Bib' },
  { key: 'rear', label: '2 Rear' },
  { key: 'front', label: '3 Front' },
];

type Props = {
  eventId: string;
  raceId: string;
  raceName?: string;
  assignmentsLocked?: boolean;
  onSaved?: (eventId: string) => void;
  /** Close the scan-and-assign window after a successful upload. */
  onClose?: () => void;
};

function downloadTextFile(fileName: string, contentType: string, text: string): void {
  const blob = new Blob([text], { type: contentType || 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'device-package-assignments.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function rowIsComplete(row: DevicePackageAssignmentCsvRow): boolean {
  return !!(row.rearDeviceId.trim() && row.frontDeviceId.trim());
}

function mergeRosterWithAssignments(
  roster: DevicePackageAssignmentCsvRow[],
  assignments: DevicePackageAssignmentCsvRow[],
): DevicePackageAssignmentCsvRow[] {
  const byRaceNo = new Map(assignments.map((r) => [r.athleteRaceNo.trim(), r]));
  const byAthleteId = new Map(assignments.map((r) => [r.athleteId.trim(), r]));
  return roster.map((base) => {
    const hit =
      byRaceNo.get(base.athleteRaceNo.trim()) ?? byAthleteId.get(base.athleteId.trim());
    if (!hit) return base;
    return {
      ...base,
      rearDeviceId: hit.rearDeviceId,
      frontDeviceId: hit.frontDeviceId,
      verificationEditedAt: hit.verificationEditedAt,
      verificationEditedStage: hit.verificationEditedStage,
      verificationRearFrom: hit.verificationRearFrom,
      verificationRearTo: hit.verificationRearTo,
      verificationFrontFrom: hit.verificationFrontFrom,
      verificationFrontTo: hit.verificationFrontTo,
    };
  });
}

function findGlobalDeviceConflict(
  rows: DevicePackageAssignmentCsvRow[],
  deviceId: string,
  skip?: { rowIndex: number; field: 'rearDeviceId' | 'frontDeviceId' },
): string | null {
  const did = deviceId.trim();
  if (!did) return null;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    for (const field of ['rearDeviceId', 'frontDeviceId'] as const) {
      if (skip && skip.rowIndex === i && skip.field === field) continue;
      if (row[field].trim() === did) {
        return `Device ID "${did}" is already used on athlete # ${row.athleteRaceNo} (${field === 'rearDeviceId' ? 'rear' : 'front'}).`;
      }
    }
  }
  return null;
}

type DeviceAssignmentConflict = {
  deviceId: string;
  message: string;
};

type DeviceAssignmentLookupRow = {
  deviceId?: string;
  eventId?: string;
  raceId?: string;
  name?: string;
  status?: string;
};

function uniqueAssignedDeviceIds(rows: DevicePackageAssignmentCsvRow[]): string[] {
  return Array.from(
    new Set(
      rows
        .flatMap((row) => [row.rearDeviceId, row.frontDeviceId])
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  );
}

export default function DevicePackageAssignmentDialog(props: Props): JSX.Element {
  const { eventId, raceId, raceName, assignmentsLocked = false, onSaved, onClose } = props;

  const [rows, setRows] = useState<DevicePackageAssignmentCsvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [latestMeta, setLatestMeta] = useState<Record<string, unknown> | null>(null);
  const [scanPhase, setScanPhase] = useState<ScanPhase>('raceNo');
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const [entryMode, setEntryMode] = useState<EntryMode>('scan');
  const [statusHint, setStatusHint] = useState('Scan athlete # or click row');
  const [confirmPullOpen, setConfirmPullOpen] = useState(false);
  const [confirmUploadOpen, setConfirmUploadOpen] = useState(false);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const scanPhaseRef = useRef<ScanPhase>(scanPhase);
  const activeRowIndexRef = useRef<number | null>(activeRowIndex);
  const rowsRef = useRef(rows);
  const rearValidationGenRef = useRef<Record<number, number>>({});
  const [rearValidation, setRearValidation] = useState<Record<number, RearDeviceValidationUiState>>(
    {},
  );
  const [rearValidationTitle, setRearValidationTitle] = useState<Record<number, string>>({});

  useEffect(() => {
    scanPhaseRef.current = scanPhase;
  }, [scanPhase]);

  useEffect(() => {
    activeRowIndexRef.current = activeRowIndex;
  }, [activeRowIndex]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const clearRearValidation = useCallback((rowIndex: number): void => {
    rearValidationGenRef.current[rowIndex] = (rearValidationGenRef.current[rowIndex] ?? 0) + 1;
    setRearValidation((prev) => {
      if (prev[rowIndex] === undefined) return prev;
      const next = { ...prev };
      delete next[rowIndex];
      return next;
    });
    setRearValidationTitle((prev) => {
      if (prev[rowIndex] === undefined) return prev;
      const next = { ...prev };
      delete next[rowIndex];
      return next;
    });
  }, []);

  const findExistingAssignmentConflicts = useCallback(
    async (deviceIds: string[]): Promise<DeviceAssignmentConflict[]> => {
      const ids = Array.from(new Set(deviceIds.map((id) => id.trim()).filter(Boolean)));
      if (!ids.length) return [];
      const api = window.eventsAPI as
        | {
            queryDeviceEventAssignments?: (deviceIds: string[]) => Promise<{
              ok?: boolean;
              data?: unknown;
              error?: string;
            }>;
          }
        | undefined;
      if (!api?.queryDeviceEventAssignments) {
        return ids.map((deviceId) => ({
          deviceId,
          message: 'Could not check whether this device is already assigned.',
        }));
      }

      const res = await api.queryDeviceEventAssignments(ids);
      if (!res?.ok) {
        return ids.map((deviceId) => ({
          deviceId,
          message: formatApiResponseError(res, 'Could not check existing device assignments'),
        }));
      }

      const assignments = Array.isArray((res.data as { assignments?: unknown[] } | undefined)?.assignments)
        ? ((res.data as { assignments: DeviceAssignmentLookupRow[] }).assignments)
        : [];
      return assignments
        .map((assignment): DeviceAssignmentConflict | null => {
          const deviceId = String(assignment?.deviceId ?? '').trim();
          if (!deviceId) return null;
          const assignedEventId = String(assignment?.eventId ?? '').trim();
          const assignedRaceId = String(assignment?.raceId ?? '').trim();
          if (assignedEventId === eventId && assignedRaceId === raceId) return null;
          const where = [
            assignment?.name ? String(assignment.name).trim() : '',
            assignedRaceId ? `race ${assignedRaceId}` : '',
            assignedEventId ? `event ${assignedEventId}` : '',
          ].filter(Boolean).join(', ');
          return {
            deviceId,
            message: where
              ? `Already assigned to ${where}.`
              : 'Already assigned to another race.',
          };
        })
        .filter((x): x is DeviceAssignmentConflict => x != null);
    },
    [eventId, raceId],
  );

  const runRearValidation = useCallback(
    async (rowIndex: number, deviceId: string): Promise<void> => {
      const trimmed = deviceId.trim();
      if (!trimmed) {
        clearRearValidation(rowIndex);
        return;
      }

      const gen = (rearValidationGenRef.current[rowIndex] ?? 0) + 1;
      rearValidationGenRef.current[rowIndex] = gen;
      setRearValidation((prev) => ({ ...prev, [rowIndex]: 'loading' }));
      setRearValidationTitle((prev) => {
        const next = { ...prev };
        delete next[rowIndex];
        return next;
      });

      const started = Date.now();
      const finishSpinner = async (): Promise<void> => {
        const elapsed = Date.now() - started;
        if (elapsed < REAR_VALIDATION_MIN_SPINNER_MS) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, REAR_VALIDATION_MIN_SPINNER_MS - elapsed);
          });
        }
      };

      const applyResult = async (result: { ok: boolean; reason?: string }): Promise<void> => {
        await finishSpinner();
        if (rearValidationGenRef.current[rowIndex] !== gen) return;
        if (result.ok) {
          setRearValidation((prev) => ({ ...prev, [rowIndex]: 'pass' }));
          setRearValidationTitle((prev) => {
            const next = { ...prev };
            delete next[rowIndex];
            return next;
          });
          return;
        }
        setRearValidation((prev) => ({ ...prev, [rowIndex]: 'fail' }));
        setRearValidationTitle((prev) => ({
          ...prev,
          [rowIndex]: result.reason ?? 'Validation failed',
        }));
      };

      try {
        const conflicts = await findExistingAssignmentConflicts([trimmed]);
        const conflict = conflicts.find((c) => c.deviceId === trimmed);
        await applyResult(
          conflict
            ? { ok: false, reason: conflict.message }
            : { ok: true },
        );
      } catch {
        await applyResult({ ok: false, reason: 'Could not check existing device assignment' });
      }
    },
    [clearRearValidation, findExistingAssignmentConflicts],
  );

  const validateAllRearDevices = useCallback(
    (nextRows: DevicePackageAssignmentCsvRow[]): void => {
      nextRows.forEach((row, i) => {
        const rear = row.rearDeviceId.trim();
        if (rear) void runRearValidation(i, rear);
        else clearRearValidation(i);
      });
    },
    [clearRearValidation, runRearValidation],
  );

  const applyRearDeviceId = useCallback(
    (rowIndex: number, deviceId: string): void => {
      setRows((prev) =>
        prev.map((r, i) => (i === rowIndex ? { ...r, rearDeviceId: deviceId } : r)),
      );
      if (deviceId.trim()) void runRearValidation(rowIndex, deviceId);
      else clearRearValidation(rowIndex);
    },
    [clearRearValidation, runRearValidation],
  );

  const loadFromApi = useCallback(async (): Promise<void> => {
    if (!window.eventsAPI) {
      dispatchToast('toast-error', 'Events API is not available');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [metaRes, athletesRes, dpaRes, raceAthletesRes] = await Promise.all([
        window.eventsAPI.getDevicePackageAssignmentsMeta(eventId, { raceId }),
        window.eventsAPI.getAthletesCsv(eventId, { raceId }),
        window.eventsAPI.getDevicePackageAssignmentsCsv(eventId, { raceId }),
        window.eventsAPI.listRaceAthletes?.(eventId, raceId) ?? Promise.resolve({ ok: false }),
      ]);
      setLatestMeta(extractRaceCsvFileMeta(metaRes));

      const dynamoAthletes = raceAthletesRes?.ok
        ? parseRaceAthletesApiResponse(raceAthletesRes.data)
        : [];
      if (dynamoAthletes.length > 0) {
        const merged = raceAthletesToPackageAssignmentRows(dynamoAthletes);
        setRows(merged);
        validateAllRearDevices(merged);
        return;
      }

      let roster: DevicePackageAssignmentCsvRow[] = [];
      if (athletesRes?.ok && athletesRes.data?.csvText) {
        const parsedAthletes = parseAthletesCsv(String(athletesRes.data.csvText));
        if (parsedAthletes.error) {
          dispatchToast('toast-error', parsedAthletes.error);
        } else {
          roster = parsedAthletes.rows.map((r) => ({
            ...r,
            rearDeviceId: '',
            frontDeviceId: '',
          }));
        }
      }

      if (!roster.length) {
        dispatchToast(
          'toast-error',
          'No athlete list found for this race. Upload athletes.csv while the race is in Draft first.',
        );
        setRows([]);
        return;
      }

      let assignments: DevicePackageAssignmentCsvRow[] = [];
      if (dpaRes?.ok && dpaRes.data?.csvText) {
        const parsedDpa = parseDevicePackageAssignmentsCsv(String(dpaRes.data.csvText));
        if (parsedDpa.error) {
          dispatchToast('toast-error', parsedDpa.error);
        } else {
          assignments = parsedDpa.rows;
        }
      }

      const merged = mergeRosterWithAssignments(roster, assignments);
      setRows(merged);
      validateAllRearDevices(merged);
    } catch (err: unknown) {
      dispatchToastError(err, 'Failed to load device packaging data');
    } finally {
      setLoading(false);
    }
  }, [eventId, raceId, validateAllRearDevices]);

  useEffect(() => {
    void loadFromApi();
  }, [loadFromApi]);

  const metaSummary = useMemo(() => {
    if (!latestMeta) return null;
    const uploadedAt = latestMeta.uploadedAt ?? latestMeta.createdAt;
    const by = latestMeta.uploadedBy ?? latestMeta.createdBy;
    const parts: string[] = [];
    if (uploadedAt != null) parts.push(`Uploaded ${formatApiDateTime(uploadedAt)}`);
    if (typeof by === 'string' && by.trim()) parts.push(`by ${by.trim()}`);
    return parts.length ? parts.join(' ') : null;
  }, [latestMeta]);

  const completedCount = useMemo(() => rows.filter(rowIsComplete).length, [rows]);
  const hasBlockingAssignmentValidation = useMemo(
    () => Object.values(rearValidation).some((state) => state === 'loading' || state === 'fail'),
    [rearValidation],
  );

  const setWorkflowPhase = useCallback((phase: ScanPhase, rowIndex: number | null, hint: string): void => {
    scanPhaseRef.current = phase;
    activeRowIndexRef.current = rowIndex;
    setScanPhase(phase);
    setActiveRowIndex(rowIndex);
    setStatusHint(hint);
  }, []);

  const selectEntryMode = useCallback((mode: EntryMode): void => {
    setEntryMode((prev) => {
      if (prev === mode) return prev;
      setWorkflowPhase('raceNo', null, mode === 'manual' ? 'Athlete # → rear → front (Enter)' : 'Scan athlete # or click row');
      return mode;
    });
  }, [setWorkflowPhase]);

  const selectPackagingRow = useCallback(
    (rowIndex: number): void => {
      if (assignmentsLocked) return;
      const bib = rowsRef.current[rowIndex]?.athleteRaceNo?.trim() ?? '';
      setWorkflowPhase('rear', rowIndex, bib ? `Athlete # ${bib} · rear` : `Row ${rowIndex + 1} · rear`);
    },
    [assignmentsLocked, setWorkflowPhase],
  );

  const advanceAfterDeviceCommitted = useCallback(
    (rowIndex: number, field: 'rearDeviceId' | 'frontDeviceId'): void => {
      const bib = rowsRef.current[rowIndex]?.athleteRaceNo?.trim() ?? '';
      if (field === 'rearDeviceId') {
        setWorkflowPhase('front', rowIndex, bib ? `Athlete # ${bib} · front` : `Row ${rowIndex + 1} · front`);
        return;
      }
      setWorkflowPhase('raceNo', null, 'Next athlete #');
    },
    [setWorkflowPhase],
  );

  const focusScanDeviceField = useCallback(
    (rowIndex: number, field: 'rearDeviceId' | 'frontDeviceId'): void => {
      if (assignmentsLocked) return;
      const phase: ScanPhase = field === 'rearDeviceId' ? 'rear' : 'front';
      const bib = rowsRef.current[rowIndex]?.athleteRaceNo?.trim() ?? '';
      setWorkflowPhase(
        phase,
        rowIndex,
        bib ? `Athlete # ${bib} · ${phase}` : `Row ${rowIndex + 1} · ${phase}`,
      );
    },
    [assignmentsLocked, setWorkflowPhase],
  );

  const commitDeviceField = useCallback(
    (
      rowIndex: number,
      field: 'rearDeviceId' | 'frontDeviceId',
      rawValue: string,
      options?: { advanceOnSuccess?: boolean },
    ): boolean => {
      const advanceOnSuccess = options?.advanceOnSuccess !== false;
      const trimmed = rawValue.trim();
      if (!trimmed) {
        setRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, [field]: '' } : r)));
        if (field === 'rearDeviceId') clearRearValidation(rowIndex);
        return false;
      }

      const role = field === 'rearDeviceId' ? 'rear' : 'front';
      const resolved = resolveRacerangerScannedDeviceId(trimmed, role);
      let deviceId = trimmed;
      if (resolved.ok) {
        deviceId = resolved.deviceId;
      } else {
        const retry = resolveFleetDeviceScan(trimmed, { role });
        if (retry.ok) {
          deviceId = retry.deviceId;
        } else if (retry.reason === 'raceranger_unparsed' || retry.reason === 'invalid_format') {
          dispatchToast('toast-error', fleetDeviceScanErrorMessage(retry.reason));
          return false;
        }
      }

      if (field === 'rearDeviceId' && !isRearDeviceId(deviceId)) {
        const message = 'Rear assignment must be a Rear_ device ID.';
        dispatchToast('toast-error', message);
        setStatusHint(message);
        return false;
      }
      if (field === 'frontDeviceId' && !isFrontDeviceId(deviceId)) {
        const message = 'Front assignment must be a Front_ device ID.';
        dispatchToast('toast-error', message);
        setStatusHint(message);
        return false;
      }

      const conflict = findGlobalDeviceConflict(rowsRef.current, deviceId, { rowIndex, field });
      if (conflict) {
        dispatchToast('toast-error', conflict);
        setStatusHint(conflict);
        return false;
      }

      if (field === 'rearDeviceId') {
        applyRearDeviceId(rowIndex, deviceId);
      } else {
        setRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, [field]: deviceId } : r)));
      }

      if (advanceOnSuccess) advanceAfterDeviceCommitted(rowIndex, field);
      return true;
    },
    [advanceAfterDeviceCommitted, applyRearDeviceId, clearRearValidation],
  );

  const mapPackagingScan = useCallback((value: string): string | null => {
    const phase = scanPhaseRef.current;
    if (phase === 'raceNo') {
      const bibResult = resolveFleetDeviceScan(value, { expectBib: true });
      if (!bibResult.ok && bibResult.reason === 'expect_bib') {
        dispatchToast('toast-error', SCAN_DEVICE_EXPECT_BIB_FIRST);
        setStatusHint(SCAN_DEVICE_EXPECT_BIB_FIRST);
        return null;
      }
      return value;
    }
    const role = phase === 'rear' ? 'rear' : 'front';
    const resolved = resolveFleetDeviceScan(value, { role });
    if (resolved.ok) return resolved.deviceId;
    const message = fleetDeviceScanErrorMessage(resolved.reason);
    dispatchToast('toast-error', message);
    setStatusHint(message);
    return null;
  }, []);

  const applyScan = useCallback(
    (raw: string): void => {
      if (assignmentsLocked) return;
      const value = raw.trim();
      if (!value) return;

      const phase = scanPhaseRef.current;
      const rowIndex = activeRowIndexRef.current;
      const currentRows = rowsRef.current;

      if (phase === 'raceNo') {
        const bibResult = resolveFleetDeviceScan(value, { expectBib: true });
        if (!bibResult.ok && bibResult.reason === 'expect_bib') {
          dispatchToast('toast-error', SCAN_DEVICE_EXPECT_BIB_FIRST);
          setStatusHint(SCAN_DEVICE_EXPECT_BIB_FIRST);
          return;
        }
        const idx = currentRows.findIndex((r) => r.athleteRaceNo.trim() === value);
        if (idx < 0) {
          setStatusHint(`No athlete with race number "${value}". Scan an athlete # from the list.`);
          dispatchToast('toast-error', `Unknown race number: ${value}`);
          return;
        }
        selectPackagingRow(idx);
        return;
      }

      if (rowIndex == null || rowIndex < 0 || rowIndex >= currentRows.length) {
        setWorkflowPhase('raceNo', null, 'Scan athlete # or click row');
        return;
      }

      const role = phase === 'rear' ? 'rear' : 'front';
      const resolved = resolveFleetDeviceScan(value, { role });
      if (!resolved.ok) {
        const message = fleetDeviceScanErrorMessage(resolved.reason);
        dispatchToast('toast-error', message);
        setStatusHint(message);
        return;
      }

      const field = phase === 'rear' ? 'rearDeviceId' : 'frontDeviceId';
      commitDeviceField(rowIndex, field, resolved.deviceId);
    },
    [assignmentsLocked, commitDeviceField, selectPackagingRow, setWorkflowPhase],
  );

  const { connectionActive, connectionPort, lastScanValue, clearLastScanValue, claim: claimDeviceScanner } =
    useBarcodeScanner({
      enabled: !assignmentsLocked,
      mapScan: mapPackagingScan,
      onScan: applyScan,
    });

  useEffect(() => {
    if (!lastScanValue) return;
    clearLastScanValue();
  }, [lastScanValue, clearLastScanValue]);

  useEffect(() => {
    if (activeRowIndex == null || !tableRef.current) return;
    const rowEl = tableRef.current.querySelector(`[data-dpa-row="${activeRowIndex}"]`);
    rowEl?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [activeRowIndex]);

  useEffect(() => {
    if (assignmentsLocked || activeRowIndex == null) return;
    if (scanPhase !== 'rear' && scanPhase !== 'front') return;
    const field = scanPhase === 'rear' ? 'rearDeviceId' : 'frontDeviceId';
    const id = window.requestAnimationFrame(() => {
      const el = tableRef.current?.querySelector(
        `[data-dpa-row="${activeRowIndex}"][data-dpa-field="${field}"]`,
      ) as HTMLInputElement | null;
      el?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeRowIndex, assignmentsLocked, scanPhase]);

  function handleManualDeviceChange(
    rowIndex: number,
    field: 'rearDeviceId' | 'frontDeviceId',
    value: string,
  ): void {
    if (entryMode !== 'manual') return;
    setRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, [field]: value } : r)));
    if (field === 'rearDeviceId') clearRearValidation(rowIndex);
  }

  async function handleUpload(): Promise<void> {
    if (!window.eventsAPI || assignmentsLocked) return;
    if (hasBlockingAssignmentValidation) {
      dispatchToast('toast-error', 'Resolve device assignment validation failures before uploading.');
      return;
    }
    const csvText = serializeDevicePackageAssignmentsCsv(rows);
    const parsed = parseDevicePackageAssignmentsCsv(csvText);
    if (parsed.error) {
      dispatchToast('toast-error', parsed.error);
      return;
    }
    const seen = new Map<string, { row: DevicePackageAssignmentCsvRow; field: 'rearDeviceId' | 'frontDeviceId' }>();
    for (const row of parsed.rows) {
      if (row.rearDeviceId.trim() && !isRearDeviceId(row.rearDeviceId.trim())) {
        dispatchToast('toast-error', `Athlete # ${row.athleteRaceNo}: rear assignment must be a Rear_ device ID.`);
        return;
      }
      if (row.frontDeviceId.trim() && !isFrontDeviceId(row.frontDeviceId.trim())) {
        dispatchToast('toast-error', `Athlete # ${row.athleteRaceNo}: front assignment must be a Front_ device ID.`);
        return;
      }
      for (const field of ['rearDeviceId', 'frontDeviceId'] as const) {
        const deviceId = row[field].trim();
        if (!deviceId) continue;
        const first = seen.get(deviceId);
        if (first) {
          dispatchToast(
            'toast-error',
            `Device ID "${deviceId}" is used more than once (${first.row.athleteRaceNo} and ${row.athleteRaceNo}).`,
          );
          return;
        }
        seen.set(deviceId, { row, field });
      }
    }
    setSaving(true);
    try {
      const conflicts = await findExistingAssignmentConflicts(uniqueAssignedDeviceIds(parsed.rows));
      if (conflicts.length > 0) {
        const conflictById = new Map(conflicts.map((conflict) => [conflict.deviceId, conflict.message]));
        setRearValidation((prev) => {
          const next = { ...prev };
          rows.forEach((row, index) => {
            const rear = row.rearDeviceId.trim();
            if (rear && conflictById.has(rear)) next[index] = 'fail';
          });
          return next;
        });
        setRearValidationTitle((prev) => {
          const next = { ...prev };
          rows.forEach((row, index) => {
            const rear = row.rearDeviceId.trim();
            const message = rear ? conflictById.get(rear) : undefined;
            if (message) next[index] = message;
          });
          return next;
        });
        dispatchToast(
          'toast-error',
          `${conflicts.length} device${conflicts.length === 1 ? '' : 's'} already assigned. Fix before uploading.`,
        );
        return;
      }
      const res = await window.eventsAPI.submitDevicePackageAssignmentsCsv(eventId, {
        raceId,
        csvText,
        fileName: 'device-package-assignments.csv',
        contentType: 'text/csv',
      });
      if (!res?.ok) {
        dispatchToast('toast-error', formatApiResponseError(res, 'Failed to upload device packaging'));
        return;
      }
      dispatchToast('toast-info', 'Device package assignments uploaded');
      onSaved?.(eventId);
      markRaceStackedOverlayJustClosed();
      setConfirmUploadOpen(false);
      onClose?.();
    } catch (err: unknown) {
      dispatchToastError(err, 'Failed to upload device packaging');
    } finally {
      setSaving(false);
      markRaceStackedOverlayJustClosed();
      window.setTimeout(() => setConfirmUploadOpen(false), 0);
    }
  }

  function closeConfirmUpload(): void {
    if (saving) return;
    markRaceStackedOverlayJustClosed();
    window.setTimeout(() => setConfirmUploadOpen(false), 0);
  }

  function closeConfirmPull(): void {
    markRaceStackedOverlayJustClosed();
    window.setTimeout(() => setConfirmPullOpen(false), 0);
  }

  const scannerStatusLine =
    entryMode === 'scan' && !connectionActive
      ? getScannerStatusText(connectionPort, false, 'Scanner')
      : '';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-600 dark:text-slate-400 min-w-0">
          <span className="font-semibold text-gray-800 dark:text-slate-200 tabular-nums">
            {completedCount}/{rows.length} paired
          </span>
          {raceName ? <span className="text-gray-500 dark:text-slate-500"> · {raceName}</span> : null}
        </p>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            className={BTN_SECONDARY}
            disabled={loading}
            onClick={() => setConfirmPullOpen(true)}
            title={metaSummary ?? 'Reload from server'}
          >
            Fetch
          </button>
          <button
            type="button"
            className={BTN_SECONDARY}
            disabled={assignmentsLocked || !rows.length}
            onClick={() =>
              downloadTextFile(
                'device-package-assignments.csv',
                'text/csv',
                serializeDevicePackageAssignmentsCsv(rows),
              )
            }
          >
            Download
          </button>
          <button
            type="button"
            className={`${BTN_PRIMARY} dark:h-8 dark:px-2.5 dark:text-[11px]`}
            disabled={assignmentsLocked || saving || !rows.length || hasBlockingAssignmentValidation}
            onClick={() => setConfirmUploadOpen(true)}
            title={
              hasBlockingAssignmentValidation
                ? 'Resolve device assignment validation failures before uploading.'
                : undefined
            }
          >
            {saving ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-2 py-1.5 text-xs text-gray-700 dark:text-slate-300">
        <ScannerEntryModeToggle
          mode={entryMode}
          onChange={selectEntryMode}
          connected={connectionActive}
          disabled={assignmentsLocked}
          embedded
        />
        <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-slate-100" title={statusHint}>
          {statusHint}
        </span>
        {entryMode === 'scan' && !assignmentsLocked ? (
          <ScanStepBreadcrumb steps={SCAN_STEPS} currentKey={scanPhase} />
        ) : null}
        {scannerStatusLine ? (
          <span className="shrink-0 text-[10px] text-amber-700 dark:text-amber-300" title={scannerStatusLine}>
            {scannerStatusLine}
          </span>
        ) : null}
      </div>

      {loading ? (
        <CyclingLoader phrases={['Loading athlete assignments…']} size="sm" className="items-start px-0 py-2" />
      ) : !rows.length ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">Add athletes in Draft first.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200 dark:border-slate-700 max-h-[min(60vh,520px)]">
          <table ref={tableRef} className="w-full min-w-[520px] border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-slate-800 text-left text-xs font-semibold text-gray-700 dark:text-slate-300">
              <tr>
                <th
                  className={[
                    'px-2 py-1.5',
                    !assignmentsLocked && entryMode === 'scan' && scanPhase === 'raceNo'
                      ? PHASE_PULSE_CELL
                      : '',
                  ].join(' ')}
                >
                  Athlete #
                </th>
                <th className="px-2 py-1.5">Name</th>
                <th className="px-2 py-1.5">Rear</th>
                <th className="px-2 py-1.5">Front</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {rows.map((row, i) => {
                const complete = rowIsComplete(row);
                const active = i === activeRowIndex;
                const showPhasePulse = !assignmentsLocked && entryMode === 'scan';
                const bibAwaiting = showPhasePulse && scanPhase === 'raceNo';
                const rearAwaiting = showPhasePulse && active && scanPhase === 'rear';
                const frontAwaiting = showPhasePulse && active && scanPhase === 'front';
                const rowBg = active
                  ? 'bg-blue-50 dark:bg-blue-950/45'
                  : complete
                    ? 'bg-emerald-50 dark:bg-emerald-950/35'
                    : 'bg-white dark:bg-slate-900';
                return (
                  <tr
                    key={`${row.athleteId}-${i}`}
                    data-dpa-row={i}
                    className={[
                      rowBg,
                      active ? 'ring-2 ring-inset ring-blue-400 dark:ring-blue-500/70' : '',
                      !assignmentsLocked ? 'cursor-pointer' : '',
                    ].join(' ')}
                    onClick={() => selectPackagingRow(i)}
                  >
                    <td
                      className={[
                        'px-2 py-1 font-mono text-xs text-gray-900 dark:text-slate-100',
                        bibAwaiting ? PHASE_PULSE_CELL : '',
                      ].join(' ')}
                    >
                      {row.athleteRaceNo}
                    </td>
                    <td className="px-2 py-1 text-xs text-gray-900 dark:text-slate-100 truncate max-w-[140px]" title={row.athleteName}>
                      {row.athleteName}
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <input
                          data-dpa-field="rearDeviceId"
                          data-dpa-row={i}
                          className={[
                            'min-w-0 flex-1 rounded border bg-white px-2 py-1 text-xs font-mono text-gray-900 dark:bg-slate-800 dark:text-slate-100',
                            rearAwaiting
                              ? PHASE_PULSE_INPUT
                              : 'border-gray-200 dark:border-slate-600',
                            entryMode === 'scan' && !assignmentsLocked ? 'cursor-pointer' : '',
                          ].join(' ')}
                          value={row.rearDeviceId}
                          readOnly={entryMode === 'scan'}
                          disabled={assignmentsLocked}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={() => {
                            focusScanDeviceField(i, 'rearDeviceId');
                            claimDeviceScanner();
                          }}
                          onChange={(e) => handleManualDeviceChange(i, 'rearDeviceId', e.target.value)}
                          onBlur={(e) => {
                            if (entryMode !== 'manual') return;
                            commitDeviceField(i, 'rearDeviceId', e.target.value, { advanceOnSuccess: false });
                          }}
                          onKeyDown={(e) => {
                            if (entryMode !== 'manual' || e.key !== 'Enter') return;
                            e.preventDefault();
                            commitDeviceField(i, 'rearDeviceId', e.currentTarget.value);
                          }}
                        />
                        <RearDeviceValidationBadge
                          state={rearValidation[i] ?? 'idle'}
                          title={rearValidationTitle[i]}
                          showLabel
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input
                        data-dpa-field="frontDeviceId"
                        data-dpa-row={i}
                        className={[
                          'w-full rounded border bg-white px-2 py-1 text-xs font-mono text-gray-900 dark:bg-slate-800 dark:text-slate-100',
                          frontAwaiting
                            ? PHASE_PULSE_INPUT
                            : 'border-gray-200 dark:border-slate-600',
                          entryMode === 'scan' && !assignmentsLocked ? 'cursor-pointer' : '',
                        ].join(' ')}
                        value={row.frontDeviceId}
                        readOnly={entryMode === 'scan'}
                        disabled={assignmentsLocked}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={() => {
                          focusScanDeviceField(i, 'frontDeviceId');
                          claimDeviceScanner();
                        }}
                        onChange={(e) => handleManualDeviceChange(i, 'frontDeviceId', e.target.value)}
                        onBlur={(e) => {
                          if (entryMode !== 'manual') return;
                          commitDeviceField(i, 'frontDeviceId', e.target.value, { advanceOnSuccess: false });
                        }}
                        onKeyDown={(e) => {
                          if (entryMode !== 'manual' || e.key !== 'Enter') return;
                          e.preventDefault();
                          commitDeviceField(i, 'frontDeviceId', e.currentTarget.value);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmPullOpen}
        stackedAboveRacePortal
        danger={false}
        title="Fetch device packaging?"
        message="Reload athlete list and device-package-assignments.csv from the server. Unsaved edits will be lost."
        confirmText="Fetch"
        onCancel={closeConfirmPull}
        onConfirm={() => {
          markRaceStackedOverlayJustClosed();
          window.setTimeout(() => {
            setConfirmPullOpen(false);
            void loadFromApi();
          }, 0);
        }}
      />

      <ConfirmDialog
        open={confirmUploadOpen}
        stackedAboveRacePortal
        danger={false}
        title="Upload device packaging?"
        message={
          hasBlockingAssignmentValidation
            ? 'One or more rear devices are already assigned elsewhere or still being checked. Fix those rows before uploading.'
            : `Upload ${rows.length} row(s) (${completedCount} complete) to the server. Partial assignments are allowed.`
        }
        confirmText="Upload"
        busy={saving}
        onCancel={closeConfirmUpload}
        onConfirm={() => void handleUpload()}
      />
    </div>
  );
}
